import type { FlexibleDate } from './medicalHistory';

// Allergy severity levels
export type AllergySeverity = 'mild' | 'moderate' | 'severe';

// Allergy category — patients know if it's a drug, food, or environmental trigger
export type AllergyCategory = 'food' | 'medication' | 'environment';

// ===== LOCAL FORM INTERFACES (UI State) =====

export interface MedicationLocal {
  localId: string; // UUID for React key
  fhirId?: string; // MedicationStatement/{id} if exists in backend
  medicationName: string;
  dosage?: string; // e.g., "10mg", "500mg"
  frequency?: string; // e.g., "Once daily", "Twice daily with meals"
  startDate?: FlexibleDate | null;
  comments?: string; // Free-text patient comments
  isDeleted?: boolean;
}

export interface SupplementLocal {
  localId: string;
  fhirId?: string;
  supplementName: string;
  dosage?: string;
  frequency?: string;
  comments?: string; // Free-text patient comments
  isDeleted?: boolean;
}

export interface AllergyLocal {
  localId: string;
  fhirId?: string; // AllergyIntolerance/{id} if exists in backend
  allergyName: string;
  category?: AllergyCategory; // Drug, food, or environmental
  reaction?: string; // e.g., "Hives", "Difficulty breathing"
  severity?: AllergySeverity;
  comments?: string;
  isDeleted?: boolean;
}

export interface MedicationsData {
  medications: MedicationLocal[];
  supplements: SupplementLocal[];
  allergies: AllergyLocal[];
}

// ===== FHIR CONSTANTS =====

// MedicationStatement status - patient-reported medications
export const MEDICATION_STATUS = {
  active: 'active',
  completed: 'completed',
  stopped: 'stopped',
} as const;

// Category to differentiate medications vs supplements
export const MEDICATION_CATEGORY = {
  medication: {
    system: 'http://terminology.hl7.org/CodeSystem/medication-statement-category',
    code: 'patientspecified',
    display: 'Patient Specified',
  },
  supplement: {
    system: 'http://precisionlongevity.io/fhir/CodeSystem/medication-category',
    code: 'supplement',
    display: 'Supplement',
  },
} as const;

// AllergyIntolerance clinical status
export const ALLERGY_CLINICAL_STATUS = {
  active: {
    system: 'http://terminology.hl7.org/CodeSystem/allergyintolerance-clinical',
    code: 'active',
    display: 'Active',
  },
} as const;

// AllergyIntolerance verification status
export const ALLERGY_VERIFICATION_STATUS = {
  unconfirmed: {
    system: 'http://terminology.hl7.org/CodeSystem/allergyintolerance-verification',
    code: 'unconfirmed',
    display: 'Unconfirmed',
  },
} as const;

// AllergyIntolerance severity
export const ALLERGY_SEVERITY_CODES = {
  mild: 'mild',
  moderate: 'moderate',
  severe: 'severe',
} as const;

// Severity display labels
export const SEVERITY_OPTIONS = [
  { value: 'mild', label: 'Mild' },
  { value: 'moderate', label: 'Moderate' },
  { value: 'severe', label: 'Severe' },
] as const;

// Allergy category options (FHIR AllergyIntolerance.category values)
export const ALLERGY_CATEGORY_OPTIONS = [
  { value: 'food', label: 'Food' },
  { value: 'medication', label: 'Drug / Medication' },
  { value: 'environment', label: 'Environmental' },
] as const;
